import * as React from 'react';
import Stack from '@mui/material/Stack';
import { FormHelperText, IconButton, Tooltip, 
  Chip, Typography,
  FormControlLabel,
  Switch,
  Divider} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined'; 

import Button from '@mui/material/Button';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { grey } from '@mui/material/colors';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import TextField from '@mui/material/TextField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import ConfirmationDialog from '../util/ConfirmationDialog';
import Breadcrumbs from '@mui/material/Breadcrumbs';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import '../Dropzone.css';
import { useDropzone } from 'react-dropzone';

import * as Utils from '../util/Utils'
import * as ATCAConstants from '../util/ATCAConstants';

import SubBandBox from './SubBandBox';
import { useLocation } from 'react-router-dom';
import { ProjectContext } from './ProjectContext';
import VeloDialog from '../components/VeloDialog';
import LeaveDetector from './LeaveDetector';
import BandInfoDialog from '../components/BandInfoDialog';

const format = "YYYYMMDDHHmmss";
dayjs.extend(utc);


enum CONFIRMATION_TYPE {
  DELETE_ZOOMS,
  SET_CORR_CONFIG,
  DELETE_CORR_SETTING,
}

export default function CorrelatorConfigurationView() {
  const projectContext = React.useContext(ProjectContext);
  const queryParams = new URLSearchParams(useLocation().search);
  const [isDirty, setIsDirty] = React.useState(false);
  const [openVeloDialog, setOpenVeloDialog] = React.useState(false);

  const [selectedCorrSetting, setSelectedCorrSetting] = React.useState(-1);

  const [showReceiverInfo, setShowReceiverInfo] = React.useState<boolean>(false);
  const [confirmation, setConfirmation] = React.useState<any>({
    showConfirmation: false,
    data: '',
    changeType: ''
  });

  // corr configuration is a list of
  // {
  //    name: string
  //    frequency_configuration: string - name,
  //    correlator_setting: string - name,
  //    sub_band_configuration: list of
  //        { band: 1, subband: 6, 
  //          zoom: zoom or spectra window config(eg: zoom 1),  centre_frequency}}
  //        }
  // }

  const { getRootProps, getInputProps } = useDropzone
  ({maxFiles: 1, 
    multiple: false,
    accept: { 'text/plain': ['.json', '.txt']},
    onDrop: acceptedFiles => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e && e.target) {
          const suffix = dayjs.utc().format(format);
          const text = e.target.result as string;
          const configs: any[] = JSON.parse(text) || [];
          // if config with same name exists then rename the new config
          const existingConfigs = projectContext.correlatorConfigurations;
          const newSettings = [...existingConfigs];
          for (const config of configs) {
            const name = config['name']||'';
            const existingName = existingConfigs.filter(
              (c) => c.name === name
            );
            if (existingName.length > 0) {
              config['name'] = name + '-' + suffix;
            }
            newSettings.push(config);
          }
          setCorrelatorSetting(newSettings);
        }
      }
      reader.readAsText(acceptedFiles[0]);
    }
  });

  const openReceiverInfo = () => {
    setShowReceiverInfo(true);
  };

  const closeReceiverInfo = () => {
    setShowReceiverInfo(false);
  };

  React.useEffect(() => {
    if (projectContext.autoSave) {
        // save changes
        if (isDirty) {
          console.log('CorrelatorConfig - auto saving');
          setIsDirty(false);
          projectContext.saveCorrelatorConfiguration();
        }
    }
  }, [projectContext, projectContext.autoSave, isDirty]);

  const saveCorrelatorSetting = () => {
    setIsDirty(false);
    projectContext.saveCorrelatorConfiguration();
  }

  const setCorrelatorSetting = (config: any[]) => {
    setIsDirty(true);
    projectContext.setCorrelatorConfigurations(config);
  }

  const updateZoomBands = (zoomBands: any[]) => {
    setSubBandConfigs(zoomBands);
  }

  const updateZoomConfiguration = (targetNames: string[], restFreqs: Number[]) => {
    const setting = JSON.stringify(projectContext.correlatorConfigurations);
    const newCorrSettings = JSON.parse(setting);
    const corrSetting = newCorrSettings[selectedCorrSetting];
    corrSetting['zoom_targets'] = targetNames;
    corrSetting['zoom_rest_frequencies'] = restFreqs;

    setCorrelatorSetting(newCorrSettings);
  }

  const setSubBandConfigs = (zoombands: any[], sort: boolean = true) => {
    const setting = JSON.stringify(projectContext.correlatorConfigurations);
    const newCorrSettings = JSON.parse(setting);
    const corrSetting = newCorrSettings[selectedCorrSetting];

    // sort subbands
    if (sort) {
      const sortedList = zoombands.sort((a: any, b: any) => {
        if (a['band'] === b['band']) {
          if (a['subband'] === b['subband']) {
            return 0;
          }
          return (a['subband'] < b['subband']) ? -1 : 1;
        }
        return a['band'] < b['band'] ? -1 : 1;
      });
      corrSetting['sub_band_configuration'] = sortedList;
    } else {
      corrSetting['sub_band_configuration'] = JSON.parse(JSON.stringify(zoombands));
    }

    setCorrelatorSetting(newCorrSettings);
  }

  const copyCorrSetting = () => {
    const newConfigs = [...projectContext.correlatorConfigurations];
    const config = projectContext.correlatorConfigurations[selectedCorrSetting];
    const copyConfig = JSON.parse(JSON.stringify(config));
    copyConfig['name'] = config['name'] + ' copy';

    newConfigs.splice(selectedCorrSetting, 0, copyConfig);
    setCorrelatorSetting(newConfigs);

    setSelectedCorrSetting(selectedCorrSetting);
  }

  const deleteCorrSetting = () => {
    setConfirmation({
      showConfirmation: true,
      data: selectedCorrSetting,
      changeType: CONFIRMATION_TYPE.DELETE_CORR_SETTING,
      title: 'Confirm delete correlator configuration',
      message: 'Do you want to delete the corrector configuration?',
    });
  }

  const setFreqConfigValue = (
    event: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>
  ) => {
    const newSettings = [...projectContext.correlatorConfigurations];
    const setting = newSettings[selectedCorrSetting];
    let freqConfig = (setting||{})['frequency_configuration']||{};
    freqConfig['centreFreq'] = Number(event.target.value);

    // update band/sub-band for each zoom
    for (let zoom of (setting['sub_band_configuration']||[])) {
      const result = Utils.caluculateBand(
        zoom['centre_frequency'], 
        freqConfig,
        getCorrSetting()
      );
      zoom['band'] = (result||{})['band']||0;
      zoom['subband'] = (result||{})['subband']||0;
    }
    setCorrelatorSetting(newSettings);
  }

  const handleBandSettingChange = (event: SelectChangeEvent) => {
    const newSettings = [...projectContext.correlatorConfigurations];
    const setting = newSettings[selectedCorrSetting];
    const bandSetting = event.target.value as string;
    const receiverName = setting['frequency_configuration']['receiver'];
    setting['frequency_configuration']['band_setting'] = bandSetting;

    if ( receiverName === '4cm') {
      const receiver = ATCAConstants.get4cmReceiver();
      setting['frequency_configuration']['centreFreq'] = Utils.getDefaultCentreFreq(
        receiver,
        bandSetting, 
      );
    }

    // update band/sub-band for each zoom
    for (let zoom of (setting['sub_band_configuration']||[])) {
      const result = Utils.caluculateBand(
        zoom['centre_frequency'], 
        setting['frequency_configuration'],
        getCorrSetting()
      );
      zoom['band'] = (result||{})['band']||0;
      zoom['subband'] = (result||{})['subband']||0;
    }
    
    setCorrelatorSetting(newSettings);
  }

  const handleCorrelatorSettingChange = (event: SelectChangeEvent) => {
    const showConfirmation = (getSelectedCorrConfig('sub_band_configuration').length > 0);

    if (!showConfirmation) {
      setCorrConfigName(event.target.value as string);
    } else {
      setConfirmation({
        showConfirmation: true,
        data: event.target.value as string,
        changeType: CONFIRMATION_TYPE.SET_CORR_CONFIG,
        title: 'Confirm correlator setting change',
        message: 'Do you want to change correlator setting? You will lose all subband configrations.',
      });
    }
  }

  const handleReceiverChange = (event: SelectChangeEvent) => {
    const newSettings = [...projectContext.correlatorConfigurations];
    const setting = newSettings[selectedCorrSetting];
    const freqConfig = (setting||{})['frequency_configuration']||{};
    const receiverName = event.target.value;
    freqConfig['receiver'] = receiverName;

    const receivers = ATCAConstants.ReceiverRange.filter(
      (rec) => {return rec['name'] === receiverName}
    )
    const receiver = receivers[0];

    // set default values for this receiver
    freqConfig['band_setting'] = receiver['default_band_setting'];
    freqConfig['centreFreq'] = Utils.getDefaultCentreFreq(
      receiver, 
      freqConfig['band_setting'], 
    );

    setCorrelatorSetting(newSettings);

    // set receiver and default centre freq and band setting
    const showConfirmation = (getSelectedCorrConfig('sub_band_configuration').length > 0);
    if (showConfirmation) {
      setConfirmation({
        showConfirmation: true,
        data: '',
        changeType: CONFIRMATION_TYPE.DELETE_ZOOMS,
        title: 'Confirm delete zoom settings?',
        message: 'You are changing receiver. Zoom settings are not longer valid. Do you want to delete them?',
      });
    }
  }
  
  const handleConfirmationNo = () => {
    setConfirmation({showConfirmation: false});
  }

  const handleConfirmationYes = () => {
    const data = confirmation['data'];

    if (confirmation['changeType'] === CONFIRMATION_TYPE.DELETE_ZOOMS) {
      setSubBandConfigs([])
    } else if (confirmation['changeType'] === CONFIRMATION_TYPE.SET_CORR_CONFIG) {
      setCorrConfigName(data);
      setSubBandConfigs([])
    } else if (confirmation['changeType'] === CONFIRMATION_TYPE.DELETE_CORR_SETTING) {
      handleDeleteCorrSetting(data);
    }
    setConfirmation({
      showConfirmation: false,
      data: null,
      changeType: null
    });
  }

  const getUTCDate = () => {
    const utcDate = getSelectedCorrConfig('')['utcDate'];
    if (utcDate) {
      return dayjs.utc(utcDate);
    } else {
      return projectContext.utcDate;
    }
  }

  const onChangeUTCDate = (newValue: any) => {
    if (newValue) {
      const newSettings = [...projectContext.correlatorConfigurations];
      const setting = newSettings[selectedCorrSetting];
      setting['utcDate'] = newValue.format();
      setCorrelatorSetting(newSettings);
    }
  }

  const handleDeleteCorrSetting = (selectedSetting: number) => {
    const newConfigs = [...projectContext.correlatorConfigurations];
    newConfigs.splice(selectedSetting, 1);
    setCorrelatorSetting(newConfigs);
    let index = selectedSetting - 1;
    index = index < 0 ? 0: index;
    setSelectedCorrSetting(index);
  }

  const setCorrConfigName = (corrConfigName: string) => {
    const newCorrSettings = [...projectContext.correlatorConfigurations];
    const corrSetting = newCorrSettings[selectedCorrSetting];
    corrSetting['correlator_setting'] = corrConfigName;
    setCorrelatorSetting(newCorrSettings);
  }

  const handleSelectSetting = (index: number) => {
    setSelectedCorrSetting(index);
  };

  const addNewCorrSetting = () => {
    const newSettings = [...projectContext.correlatorConfigurations];
    newSettings.push({
      name: 'untitled',
      correlator_setting: '',
      frequency_configuration: {},
      sub_band_configuration: [],
      utcDate: projectContext.utcDate.format()
    });

    setCorrelatorSetting(newSettings);
    setSelectedCorrSetting(newSettings.length - 1);
  }

  const closeVeloDialog = () => {
    setOpenVeloDialog(false);
  }

  const getReceiver = () => {
    const freqConfig = getSelectedCorrConfig('frequency_configuration');
    const receiverName = freqConfig['receiver'];

    if (receiverName) {
      const receivers = ATCAConstants.ReceiverRange.filter((rec: any) => {
        return (rec['name'] === receiverName);
      });

      if (receivers.length > 0) {
        return receivers[0]
      } else {
        return null;
      }
    }

    return null;
  }

  const getBandSetting = () => {
    const freqConfig = getSelectedCorrConfig('frequency_configuration');
    const band_setting_name = freqConfig['band_setting'] || '';
    const band_setting = {};
    const receiverName = freqConfig['receiver'];

    if (band_setting_name) {
      const settings = ATCAConstants.BAND_SETTINGS.filter((setting: any) => {
        return ((setting['name'] === band_setting_name) 
        && receiverName && (setting['compatible_receivers'][receiverName]));
      });

      if (settings.length > 0) {
        return settings[0]
      } else {
        return null;
      }
    }

    return band_setting;
  }

  const getSelectedCorrConfig = (name: string) => {
    if (projectContext.correlatorConfigurations.length <= 0) {
      return {};
    }

    if (!name) {
      if (selectedCorrSetting<0)
        return {};
      else if (selectedCorrSetting < 0)
        return {};
      else
        return projectContext.correlatorConfigurations[selectedCorrSetting];
    }
    if (selectedCorrSetting < 0) {
      if (name === 'sub_band_configuration')
        return [];

      return {};
    }

    return projectContext.correlatorConfigurations[selectedCorrSetting][name];
  }

  const handleAdvanceModeChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newSettings = [...projectContext.correlatorConfigurations];
    const setting = newSettings[selectedCorrSetting];
    setting['advance_mode'] = event.target.checked;

    setCorrelatorSetting(newSettings);
  }

  const setCorrSettingName = 
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newSettings = [...projectContext.correlatorConfigurations];
    const setting = newSettings[selectedCorrSetting];
    setting['name'] = event.target.value;

    setCorrelatorSetting(newSettings);
  }



  const hasSubBands = (): boolean => {
    return projectContext.hasSubBands(getSelectedCorrConfig(''));
  }

  const getCorrSetting = () => {
    const corrSettingName = getSelectedCorrConfig('correlator_setting');
    const corrConfig = getSelectedCorrConfig(''); 
    return projectContext.getCorrelatorSetting(
      corrSettingName, (corrConfig['frequency_configuration']||{})['receiver']);
  }

  const addNewZoomBand = () => {
    const zoomBand = {
      id: Date.now(),
      band: 0,
      subband: 0,
      centre_frequency: '',
      zoom: '',
      rest_frequency: '',
      data_rate: '-',
    };

    const selectedCorrSetting = getSelectedCorrConfig('');
    const newZooms: any[] = [...selectedCorrSetting['sub_band_configuration']];
    newZooms.unshift(zoomBand);

    setSubBandConfigs(newZooms, false);
  }

  const getIsBeforeToday = () => {
    const date = getUTCDate();
    const today = dayjs.utc().hour(0).minute(0).second(0).millisecond(0);

    if (date.isBefore(today)) {
      return true;
    }

    return false;
  }

  const CustomModeBox = (config: any, freqValidation: any, freqConfig: any) => (
    <Stack spacing={0}>
      <BandInfoDialog open={showReceiverInfo} handleClose={closeReceiverInfo}></BandInfoDialog>
      <Stack direction={'row'} spacing={2} 
        sx={{ alignItems: 'baseline'}}>

        <TextField id="band-config-name" label="Name" 
          variant="standard" sx={{ width: '35%' }}
          value={config['name'] || ''}
          onChange={setCorrSettingName}
        />

        <FormControl variant="standard" sx={{ width: '20%' }}
          error={!getReceiver()}
        >
          <InputLabel id="receiver">Receiver</InputLabel>
          <Select
            labelId="receiver-label"
            id="receiver-select-value"
            label="Receiver"
            onChange={handleReceiverChange}
            value={((config['frequency_configuration']||{})['receiver'])||''}
            renderValue={(selected) => selected}
            displayEmpty={true}
          >
            {ATCAConstants.ReceiverRange
            .map((receiver: any) => (
              <MenuItem key={receiver['name']} value={receiver['name']}>
                <ListItemText primary={receiver['name']}
                  secondary={
                    <Typography variant="body2">
                      { receiver['start'] + ' - ' + receiver['end'] + ' MHz'}
                    </Typography>
                  }
                />
              </MenuItem>
            ))}
          </Select>
          {
            (!getReceiver())
            &&
            <FormHelperText>Unknown receiver</FormHelperText>
          }   
        </FormControl>

        <TextField required variant="standard" 
          label="Centre Freq (MHz)" sx={{ height: '80px', width: '20%' }}
          id={`centre_freq`}
          value={((config['frequency_configuration']||{})['centreFreq'])||''}
          error={!Boolean(freqValidation['isValid'])}
          helperText={freqValidation['message'] }
          onChange={setFreqConfigValue} 
          disabled={(
            !(config['frequency_configuration']||{})['receiver']
            ||
            (config['frequency_configuration']||{})['receiver'] === '4cm'
          )}/>

        <FormControl variant="standard" sx={{ width: '30%' }}
          error={
            (!getBandSetting()) ||
            ((getBandSetting()||{})['status'] === 'obsolete')
          }
        >
          <InputLabel id="band-config">Band Setting</InputLabel>
          <Select
            labelId="band-setting-label"
            id="band-setting-select-value"
            label="Band Setting"
            onChange={handleBandSettingChange}
            value={((config['frequency_configuration']||{})['band_setting'])||''}
            renderValue={(selected) => selected}
            displayEmpty={true}
          >            
            {ATCAConstants.BAND_SETTINGS
            .filter((setting: any) => { 
              return(
                (config['frequency_configuration']||{})['receiver']
                &&
                setting['compatible_receivers'][config['frequency_configuration']['receiver']]
              )
            })
            .map((setting: any) => (
              <MenuItem key={setting['name']} value={setting['name']}
                disabled={setting['status'] === 'obsolete'}
              >
                <ListItemText primary={
                  setting['name'] + 
                  (setting['status'] === 'obsolete' ? ' (obsolete)' : '')
                }
                  secondary={
                    <Stack width={'100%'} direction="row" spacing={2}>
                      <Typography variant="body2">
                        { setting['band1_offset'] >=0 ? setting['band1_offset'] : '-' }
                      </Typography>
                      <Typography variant="body2">
                        { setting['band2_offset'] >=0 ? setting['band2_offset'] : '-' }
                      </Typography>
                      <Typography variant="body2">
                        { setting['band3_offset'] >=0 ? setting['band3_offset'] : '-' }
                      </Typography>
                      <Typography variant="body2">
                        { setting['band4_offset'] >=0 ? setting['band4_offset'] : '-' }
                      </Typography>
                    </Stack>
                  }
                />
              </MenuItem>
            ))}
          </Select>
          {
            (!getBandSetting())
            &&
            <FormHelperText>Unknown band setting</FormHelperText>
          }
          {
            ((getBandSetting()||{})['status'] === 'obsolete')
            &&
            <FormHelperText>Obsolete band setting</FormHelperText>
          }
        </FormControl>

        <Tooltip title="View receiver help">
          <Chip label={'?'} 
            size="medium" key={`freq-receiver`} onClick={openReceiverInfo}
            sx={{color: 'black', backgroundColor: grey[100]}}/>
        </Tooltip>

        <FormControlLabel
          control={<Switch size="small"
            checked={Boolean(config['advance_mode'])} 
            onChange={handleAdvanceModeChange}
            name="advance-mode" />
          }
          label="Advance Mode"
        />

      </Stack>

      <Stack direction={'row'} justifyContent={'space-between'} 
        alignItems={'center'} spacing={2}>
                
        <FormControl sx={{ width: '40%' }} variant="standard" 
          error={
            (Object.keys(getCorrSetting()).length === 0) ||
            (getCorrSetting()['status'] === 'obsolete')
          }
        >
          <InputLabel id="cor-config">Correlator setting</InputLabel>
          <Select
            labelId="cor-config-label"
            id="cor-config-select-value"
            label="Correlator setting"
            onChange={handleCorrelatorSettingChange}
            value={getSelectedCorrConfig('correlator_setting')||''}
            renderValue={(selected) => selected}
            displayEmpty={true}
          >
            <Typography variant='h6' color={'primary'} align="center">Normal</Typography>
            {projectContext.correlatorSettings.filter(
              (setting: any) => {
                const receiver = (config['frequency_configuration']||{})['receiver'];
                return (setting['compatible_receivers'][receiver] 
                          && setting['config_type'] === 'normal');
              }
            ).map((config: any) => (
              <MenuItem key={config['name']} value={config['name']}
                disabled={config['status'] === 'obsolete'}
              >
                <ListItemText primary={
                  config['name'] + 
                  (config['status'] === 'obsolete' ? ' (obsolete)' : '')
                }
                  secondary={
                    <Stack width={'100%'} direction="row" spacing={2}>
                      <Typography variant="body2">
                        {config['spectral_points_per_subband']} points  
                      </Typography>
                      <Typography variant="body2">
                        {Math.round(config['spectral_resolution']*1000)/1000}{config['spectral_resolution_unit']}
                      </Typography>
                      <Typography variant="body2">
                        {config['data_rate']/1000}MB/sec
                      </Typography>
                    </Stack>
                  }
                />
              </MenuItem>
            ))}

            {
              Boolean(config['advance_mode'])
              &&
              <React.Fragment>
                <Divider />
                <Typography variant='h6' color={'primary'} align="center">Advanced</Typography>
                {
                  projectContext.correlatorSettings.filter(
                    (setting: any) => {
                      const receiver = (config['frequency_configuration']||{})['receiver'];
                      return (setting['compatible_receivers'][receiver] 
                                && setting['config_type'] === 'advanced');
                    }
                  ).map((config: any) => (
                    <MenuItem key={config['name']} value={config['name']}
                      disabled={config['status'] === 'obsolete'}
                    >
                      <ListItemText primary={
                        config['name'] + 
                        (config['status'] === 'obsolete' ? ' (obsolete)' : '')
                      }
                        secondary={
                          <Stack width={'100%'} direction="row" spacing={2}>
                            <Typography variant="body2">
                              {config['spectral_points_per_subband']} points  
                            </Typography>
                            <Typography variant="body2">
                              {Math.round(config['spectral_resolution']*1000)/1000}{config['spectral_resolution_unit']}
                            </Typography>
                            <Typography variant="body2">
                              {config['data_rate']/1000}MB/sec
                            </Typography>
                          </Stack>
                        }
                      />
                    </MenuItem>
                  ))
                }
              </React.Fragment>
            }
          </Select>
          {
            Object.keys(getCorrSetting()).length === 0
            &&
            <FormHelperText>Unknown correlator configuration</FormHelperText>
          }        
          {
            getCorrSetting()['status'] === 'obsolete'
            &&
            <FormHelperText>Obsolete correlator configuration</FormHelperText>
          }             
        </FormControl>
        {
          hasSubBands() &&
          <>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
              <DatePicker label="UTC date"
                sx={{ width: '15%', minWidth: '150px' }}
                format='YYYY-MM-DD'
                value={getUTCDate()}
                onChange={(e) => onChangeUTCDate(e)}
                slotProps={{
                  textField: {
                    error: getIsBeforeToday(),
                    helperText: getIsBeforeToday() ? 'Date is in the past' : '',
                  },                  
                  actionBar: {
                    actions: ['today'],
                  },
                }}
              />
            </LocalizationProvider>
          
            <Button variant="text"
              disabled={!hasSubBands()}
              onClick={addNewZoomBand}
              startIcon={<ControlPointIcon />}>
              Add Zoom Band
            </Button>

            <Button variant="outlined" 
              disabled={!hasSubBands()}
              onClick={e => setOpenVeloDialog(true)}>
              Open Velo
            </Button>
          </>
        }
      </Stack>

      <SubBandBox
        corrSetting={getCorrSetting()}
        corrConfig={getSelectedCorrConfig('')}
        setSubBandConfigs={setSubBandConfigs}
        utcDate={getSelectedCorrConfig('utcDate')}
        bandSetting={getBandSetting()}
      />
    </Stack>
  );

  const handleDownload = () => {
    let filename = 'correlator_config.json';
    var element = document.createElement('a');
    const correlatorConfig = projectContext.correlatorConfigurations;
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' 
      + JSON.stringify(correlatorConfig, null, 4));
    element.setAttribute('download', filename);

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <LeaveDetector isDirty={isDirty} >
      <Stack id='sub-band-view' 
        margin={'10px'} height={'calc(100vh - 100px)'}>

        <VeloDialog
          updateZoomBands={updateZoomBands}
          updateZoomConfiguration={updateZoomConfiguration}
          corrConfig={getSelectedCorrConfig('')}
          open={openVeloDialog}
          handleClose={closeVeloDialog}
          utcDate={getSelectedCorrConfig('')['utcDate']}
        />

        <ConfirmationDialog open={confirmation.showConfirmation}
          title={confirmation['title']}
          message={confirmation['message']}
          handleNo={handleConfirmationNo}
          handleYes={handleConfirmationYes}
        />

        <Stack direction={'row'} alignItems={'center'} padding={'0 0 10px 0'}
          justifyContent={'space-between'} spacing={2}>
          <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{flexGrow: 1}}>
            <Typography key="project" variant="subtitle1" color="primary">
              {queryParams.get('project')}
            </Typography>

            <Typography key="file" variant="h6" 
              color={ isDirty ? "#c62828" : "primary"}>
              {'Correlator Configuration'}
            </Typography>
          </Breadcrumbs>

          <Button variant="text" onClick={addNewCorrSetting}
            startIcon={<ControlPointIcon />}>
            Add Setting
          </Button>

          <Stack direction={'row'} spacing={3} alignItems={'center'} 
            justifyContent="flex-end" flexGrow={4}>

            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
                <Stack spacing={0}>
                  <Typography variant="button"
                    width={'100%'} textAlign='center'>
                    Import Correlator Config JSON file
                  </Typography>
                  <Typography className='hide' variant="caption" textAlign='center'>
                    Drag 'n drop ONE .json file here, or click to select a file
                  </Typography>
                </Stack>
            </div>

            <Tooltip title="Download Correlator configuration">
                <Button variant="text" onClick={handleDownload}
                  startIcon={<SystemUpdateAltOutlinedIcon />}>
                Download
                </Button>
            </Tooltip>

            <Tooltip title="Save correlator changes to server">
              <Button variant="text" onClick={saveCorrelatorSetting}
                startIcon={<SaveIcon />} aria-label="save" color='primary'>
                Save
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack spacing={2} direction={'row'}
          sx={{
            flexGrow: 4,
            border: 'solid 1px #eeeeee',
            borderRadius: '5px',
            height: 'calc(100% - 80px)',
            overflow: 'hidden'
          }}>
          <Box sx={{ width: '25%', height: '100%', overflow: 'scroll',
                      backgroundColor: '#fafafa'}}>
            <List sx={{ padding: 0}}>
              {projectContext.correlatorConfigurations.map((row, index) => (
                <ListItem
                  key={'corr-setting-' + index}
                  tabIndex={0}
                  onClick={e => handleSelectSetting(index)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSelectSetting(index);
                    }
                  }}
                  className={`${index === selectedCorrSetting ? 'selected' : ''}`}
                  secondaryAction={
                    (index === selectedCorrSetting) &&
                    <Stack spacing={1} direction={'row'}>
                      <IconButton
                        sx={{ "&:hover": { color: '#c62828' } }}
                        onClick={deleteCorrSetting} edge="end" 
                        aria-label="delete correlator setting">
                        <DeleteOutlineIcon />
                      </IconButton>
                      <IconButton size='small'
                        sx={{ "&:hover": { color: '#c62828' } }}
                        onClick={copyCorrSetting} edge="end" 
                        aria-label="copy correlator setting">
                        <ContentCopyOutlinedIcon />
                      </IconButton>
                    </Stack>
                  }>
                  <ListItemText
                    primary={row['name']}
                    secondary={
                      <Typography variant="body2" 
                        className={`data-rate ${index === selectedCorrSetting ? 'selected' : ''}`}>
                        {projectContext.calculateDataRate(row['name'],
                          row['frequency_configuration']['receiver']
                        )} kB/s
                      </Typography>
                    } />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box
            sx={{ width: '80%', padding: '15px'}}
            style={{margin: 0, borderLeft: 0}}
          >
            {
              (selectedCorrSetting>=0 && projectContext.correlatorConfigurations.length > 0)
              && 
              CustomModeBox(
                getSelectedCorrConfig(''), 
                Utils.validateFreq(getSelectedCorrConfig('frequency_configuration')),
                getSelectedCorrConfig('frequency_configuration')
              )
            }
          </Box>
        </Stack>
      </Stack>
    </LeaveDetector>
  )
}


